/**
 * What the players see when the timer runs out.
 *
 * Three jobs, in order of who needs them. The players need a score and a sense
 * of how the round went. The person running the playtest needs the session id,
 * because that is the name of the log on the box — it is kept on the card as
 * `data-session-id`, where tools and devtools can read it, rather than shown to
 * players as a string that means nothing to them. And both need a way back in, which is the same
 * `ready` the lobby uses — the room comes back under a fresh session id, so a
 * second round is a second artifact rather than two rounds in one log.
 *
 * Nothing here is a `<canvas>`: the round is over, so there is no continuous
 * value left to animate and no reason to bypass React.
 */

import type { RoundTally } from '../feed/feed'

export interface RoundEndProps {
  /** `null` for a round this page arrived after — it never saw the score. */
  score: number | null
  reason: 'timer' | 'aborted'
  tally: RoundTally
  sessionId: string | null
  /** True once this player has asked for another round. */
  waiting: boolean
  onPlayAgain(): void
}

export function RoundEnd({ score, reason, tally, sessionId, waiting, onPlayAgain }: RoundEndProps) {
  return (
    <section className="gate round-end" data-session-id={sessionId ?? undefined}>
      <p className="gate-step">{reason === 'timer' ? "Time's up" : 'Ended early'}</p>
      <h1>{reason === 'timer' ? 'Round over' : 'Round ended early'}</h1>

      {score === null ? (
        <p className="hint">This round ended before you got here. Press Play again to start another.</p>
      ) : (
        <>
          <p className="final-score">
            <span className="final-score-value">{score}</span>
            <span className="final-score-label">final score</span>
          </p>

          <ul className="round-summary">
            <li className="tally-good">
              <strong>{tally.matched}</strong> orders served
            </li>
            <li>
              <strong>{tally.served - tally.matched}</strong> dishes nobody ordered
            </li>
            <li className="tally-bad">
              <strong>{tally.expired}</strong> orders missed
            </li>
          </ul>
        </>
      )}

      {reason === 'aborted' && score !== null && (
        <p className="hint">Someone left before time ran out.</p>
      )}

      <div className="gate-actions">
        <button type="button" onClick={onPlayAgain} disabled={waiting}>
          {waiting ? 'Waiting for your partner…' : 'Play again'}
        </button>
      </div>
    </section>
  )
}
